<template>
  <v-dialog v-model="active" max-width="920" scrollable persistent>
    <v-card class="csm">
      <div class="csm__head">
        <div>
          <span class="csm__eyebrow">Dashboard standard</span>
          <div class="csm__title">
            <v-icon small class="mr-2">mdi-palette-swatch-outline</v-icon>
            <span>{{ view === VIEW.EDIT ? (editingId ? 'Edit coloring schema' : 'Create coloring schema') : 'Coloring Schemas' }}</span>
          </div>
          <p class="csm__subtitle">
            {{ subtitleText }}
          </p>
        </div>
        <v-btn icon small @click="close" aria-label="Close">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text id="csm-body" class="csm__body">
        <!-- Loading the tab's configuration -->
        <div v-if="loading" class="csm__center">
          <envizion-loader :size="40" :color="$vuetify.theme.dark ? '#2cc295' : '#03624c'" label="Loading schemas" />
        </div>

        <!-- LIST -->
        <template v-else-if="view === VIEW.LIST">
          <!-- Load failed: fail closed. Creating now would save a new schema over ones that only
               failed to load (persist merges the whole array), so offer Retry instead of Create. -->
          <div v-if="loadError" class="csm__empty">
            <v-icon size="34" color="warning" class="csm__empty-icon">mdi-alert-circle-outline</v-icon>
            <p class="csm__empty-text">{{ loadError }}</p>
            <v-btn color="primary" class="csm__empty-action" :loading="loading" @click="open">
              <v-icon small left>mdi-refresh</v-icon> Retry
            </v-btn>
          </div>

          <div v-else-if="!schemas.length" class="csm__empty">
            <v-icon size="34" class="csm__empty-icon">mdi-palette-outline</v-icon>
            <p class="csm__empty-text">No coloring schemas on this tab yet. Create one to give values a consistent meaning across widgets.</p>
            <v-btn color="primary" class="csm__empty-action" @click="startCreate">
              <v-icon small left>mdi-plus</v-icon> Create schema
            </v-btn>
          </div>

          <div v-else class="csm__list">
            <div v-for="schema in schemas" :key="schema.id" class="csm__row">
              <div class="csm__row-main">
                <div class="csm__row-name">{{ schema.name }}</div>
                <div class="csm__row-meta">{{ schemaKindLabel(schema) }}<span v-if="!isCategorySchema(schema) && schema.unit"> · {{ unitLabel(schema.unit) }}</span></div>
                <div class="csm__strip">
                  <span v-for="(band, bi) in bandPreview(schema)" :key="bi" class="csm__band">
                    <span class="csm__swatch" :style="{ background: band.swatch }"></span>{{ band.label }}
                  </span>
                </div>
              </div>
              <div class="csm__row-actions">
                <v-btn icon small title="Edit schema" @click="startEdit(schema)">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn icon small title="Delete schema" @click="startDelete(schema)">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <v-btn v-if="schemas.length" outlined small color="primary" class="csm__add" @click="startCreate">
            <v-icon small left>mdi-plus</v-icon> New schema
          </v-btn>
        </template>

        <!-- CREATE / EDIT -->
        <template v-else-if="view === VIEW.EDIT">
          <div class="csm__kind">
            <span class="csm__label">Schema type</span>
            <v-btn-toggle v-model="editKind" mandatory class="csm__kind-toggle">
              <v-btn v-for="opt in kindOptions" :key="opt.value" :value="opt.value" small>
                <v-icon small left>{{ opt.icon }}</v-icon>{{ opt.label }}
              </v-btn>
            </v-btn-toggle>
            <span class="csm__hint">
              {{ isEditingCategory
                ? 'Match each label your data returns to a color.'
                : 'Split a numeric measure into connected value ranges.' }}
            </span>
          </div>

          <div class="csm__form-grid" :class="{ 'csm__form-grid--single': isEditingCategory }">
            <div>
              <label for="csm-schema-name" class="csm__label">Schema name</label>
              <v-text-field
                id="csm-schema-name"
                v-model="editName"
                placeholder="e.g. Corrosion severity"
                maxlength="60"
                outlined
                dense
                hide-details
                class="csm__field">
              </v-text-field>
            </div>

            <div v-if="!isEditingCategory">
              <label for="csm-schema-unit" class="csm__label">Unit</label>
              <v-combobox
                id="csm-schema-unit"
                v-model="editUnit"
                :items="unitSuggestions"
                outlined
                dense
                hide-details
                clearable
                placeholder="e.g. mm/yr — or leave blank for unitless"
                :menu-props="{ offsetY: true, zIndex: 2100 }"
                class="csm__field">
              </v-combobox>
            </div>
          </div>
          <span v-if="!isEditingCategory" class="csm__hint">Pick a suggested unit for automatic conversion, or type a custom one as a display-only label. Leave blank for unitless. Every boundary uses this unit.</span>

          <!-- CATEGORY: independent label colors -->
          <template v-if="isEditingCategory">
            <div class="csm__ranges-head">
              <div>
                <span class="csm__label">Label colors</span>
                <span class="csm__hint csm__hint--inline">Give each data label its own color. Rows are independent.</span>
              </div>
            </div>
            <schema-entries-editor v-model="editEntries"></schema-entries-editor>
          </template>

          <!-- NUMBER: connected value ranges -->
          <template v-else>
            <div class="csm__ranges-head">
              <div>
                <span class="csm__label">Value ranges</span>
                <span class="csm__hint csm__hint--inline">Give each range a label and color. Boundaries connect automatically.</span>
              </div>
              <span class="csm__ranges-badge"><v-icon x-small>mdi-link-variant</v-icon> Connected</span>
            </div>
            <schema-bands-editor v-model="editBands"></schema-bands-editor>
          </template>

          <p v-if="actionError" class="csm__error csm__error--mt" role="alert">
            <v-icon x-small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>{{ actionError }}
          </p>
          <span v-else-if="isEditingCategory" class="csm__hint csm__hint--mt">
            Entry values must match the labels your data returns (matched trimmed and case-insensitive). Labels with no matching entry render grey.
          </span>
          <span v-else class="csm__hint csm__hint--mt">
            Custom fills are labelled in widgets. Semantic colors remain available for good, caution, and critical states.
          </span>
        </template>

        <!-- DELETE CONFIRM -->
        <template v-else-if="view === VIEW.CONFIRM_DELETE">
          <div class="csm__confirm">
            <v-icon size="30" color="warning" class="mb-2">mdi-alert-outline</v-icon>
            <p class="csm__confirm-title">Delete "{{ deleteTarget && deleteTarget.name }}"?</p>
            <p class="csm__confirm-text">
              Widgets on this tab that use this schema will show as unavailable until you pick another color schema for them.
            </p>
            <p v-if="actionError" class="csm__error" role="alert">
              <v-icon x-small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>{{ actionError }}
            </p>
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="csm__foot">
        <!-- Negative action left, primary right -->
        <template v-if="view === VIEW.LIST">
          <v-spacer></v-spacer>
          <v-btn text @click="close">Close</v-btn>
        </template>
        <template v-else-if="view === VIEW.EDIT">
          <v-btn outlined @click="backToList" :disabled="saving">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed :loading="saving" @click="saveSchema">
            {{ editingId ? 'Save changes' : 'Create schema' }}
          </v-btn>
        </template>
        <template v-else-if="view === VIEW.CONFIRM_DELETE">
          <v-btn outlined @click="backToList" :disabled="saving">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" depressed :loading="saving" @click="doDelete">Delete schema</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SchemaBandsEditor from './SchemaBandsEditor.vue';
import SchemaEntriesEditor from './SchemaEntriesEditor.vue';
import dashboardConfigurationService from '../service/dashboard-configuration.service';
import { $uuidv4 } from '@Utils';
import { log, error } from '@/utils/debugLogger';
import {
  UNIT, APPLIES_TO, SCHEMA_SOURCE, TONE_SWATCH, NEUTRAL_SWATCH, paintSwatch, validateSchema,
} from '@/features/Dashboard/utils/coloringSchemas';
import { numOrNull } from '@/features/Dashboard/utils/numberCoerce';

// The config JSON version this manager writes (COLORING_SCHEMA_REGISTRY.md §Config JSON).
const CONFIG_FORMAT_VERSION = 1;

// View-mode discriminator for the dialog body (schema list | create-edit sub-form | delete confirm).
// Frozen enum so the same three strings are not repeated across the template and the script.
const VIEW = Object.freeze({ LIST: 'list', EDIT: 'edit', CONFIRM_DELETE: 'confirmDelete' });

export default {
  name: 'ColoringSchemasManager',

  components: { SchemaBandsEditor, SchemaEntriesEditor },

  props: {
    /** The current tab's id (DashboardPage id). Keys the configuration GET/PUT. */
    tabId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      active: false,
      loading: false,
      saving: false,
      view: VIEW.LIST,
      // Working copy of the coloringSchemas array; loaded from the server on open() and replaced
      // after a successful save. The full config blob is NOT cached here: every save re-reads it
      // fresh (updateTabConfiguration) so a stale snapshot can never clobber sibling keys.
      schemas: [],
      loadError: '',
      // Create/edit sub-form
      editingId: null, // null = creating; a schema id = editing (keeps that id on save)
      editName: '',
      editKind: APPLIES_TO.NUMBER, // 'number' (connected bands) | 'category' (label colors)
      editUnit: UNIT.COUNT,
      editBands: [],
      editEntries: [],
      actionError: '',
      // Delete
      deleteTarget: null,
    };
  },

  computed: {
    /** The view-mode enum, exposed so the template can compare against VIEW.* (frozen, never changes). */
    VIEW() {
      return VIEW;
    },

    /** Suggested units for the combobox — the known UNIT enum values (picking one keeps unit
     *  conversion working). The field also accepts arbitrary free text and blank (see buildSchema). */
    unitSuggestions() {
      return Object.values(UNIT);
    },

    /** True while the edit sub-form is authoring a category (label→color) schema. */
    isEditingCategory() {
      return this.editKind === APPLIES_TO.CATEGORY;
    },

    /** Segmented "Schema type" toggle options (value bound to the appliesTo enum, no magic strings). */
    kindOptions() {
      return [
        { value: APPLIES_TO.NUMBER, label: 'Number bands', icon: 'mdi-tune-vertical' },
        { value: APPLIES_TO.CATEGORY, label: 'Label colors', icon: 'mdi-tag-multiple-outline' },
      ];
    },

    /** Header subtitle — kind-aware in the edit view so category copy never mentions band boundaries. */
    subtitleText() {
      if (this.view !== VIEW.EDIT) return 'Named value-to-color rules for this dashboard.';
      return this.isEditingCategory
        ? 'Give each data label a consistent color across linked widgets.'
        : 'Set connected value ranges that every linked widget can use.';
    },
  },

  created() {
    // Non-reactive control state (this instance is persistent — a parent keeps one ref and only
    // toggles `active`). `_openToken` is bumped on every open() so a late GET/PUT callback from a
    // superseded session bails instead of writing another tab's config; `_sessionTabId` is the tab
    // bound at open() time so persist() targets it, not the live `tabId` prop which may have moved on.
    this._openToken = 0;
    this._sessionTabId = null;
  },

  methods: {
    /** Open the manager for the CURRENT tab and load its schemas. Binds this tab id + a fresh
     *  generation token so a GET that resolves after the user switches tabs (or reopens) is ignored,
     *  never overwriting the instance with the wrong tab's schemas. */
    open() {
      this.active = true;
      this.view = VIEW.LIST;
      this.actionError = '';
      this.loadError = '';
      this.deleteTarget = null;
      const tabId = this.tabId;
      const token = ++this._openToken;
      this._sessionTabId = tabId;
      if (!tabId) {
        this.schemas = [];
        return;
      }
      this.loading = true;
      dashboardConfigurationService.getConfiguration(tabId)
        .then((res) => {
          if (token !== this._openToken) return; // a newer open() superseded this GET
          const parsed = this.parseConfig(res);
          this.schemas = Array.isArray(parsed.coloringSchemas) ? parsed.coloringSchemas.map(s => ({ ...s })) : [];
        })
        .catch((err) => {
          if (token !== this._openToken) return; // stale failure from a superseded session
          error('ColoringSchemasManager', 'load configuration failed', tabId, err);
          this.schemas = [];
          // Fail closed: creating a schema now would save over ones that only failed to load (the
          // save merges the whole array), so the list view offers Retry instead of Create.
          this.loadError = 'Could not load this tab\'s schemas. Retry before making changes.';
        })
        .finally(() => {
          if (token !== this._openToken) return; // don't flip a newer session's loading flag
          this.loading = false;
        });
    },

    close() {
      this.active = false;
    },

    backToList() {
      this.view = VIEW.LIST;
      this.actionError = '';
      this.deleteTarget = null;
    },

    /** Parse the GET response envelope (tolerant, like CustomDashboard's parser) into a config object. */
    parseConfig(res) {
      const body = res && res.data;
      let raw = null;
      if (body) {
        if (body.configuration !== undefined) raw = body.configuration;
        else if (body.data && body.data.configuration !== undefined) raw = body.data.configuration;
      }
      if (raw == null || raw === '') return {};
      try {
        return typeof raw === 'string' ? (JSON.parse(raw) || {}) : raw;
      } catch (err) {
        error('ColoringSchemasManager', 'configuration unparseable', err);
        return {};
      }
    },

    startCreate() {
      this.editingId = null;
      this.editName = '';
      this.editKind = APPLIES_TO.NUMBER;
      this.editUnit = UNIT.COUNT;
      // Seed BOTH editors so toggling the schema type never loses in-progress work.
      this.editBands = this.defaultBands();
      this.editEntries = this.defaultEntries();
      this.actionError = '';
      this.view = VIEW.EDIT;
    },

    startEdit(schema) {
      // Edit is read-modify-write: the id is PRESERVED so every widget referencing it keeps resolving.
      this.editingId = schema.id;
      this.editName = schema.name || '';
      this.editKind = schema.appliesTo === APPLIES_TO.CATEGORY ? APPLIES_TO.CATEGORY : APPLIES_TO.NUMBER;
      this.editUnit = schema.unit || UNIT.COUNT;
      // Seed BOTH editors from the stored schema (the inactive kind falls back to its defaults).
      this.editBands = this.rangesToBands(Array.isArray(schema.ranges) ? schema.ranges : []);
      this.editEntries = this.entriesToRows(Array.isArray(schema.entries) ? schema.entries : []);
      this.actionError = '';
      this.view = VIEW.EDIT;
    },

    startDelete(schema) {
      this.deleteTarget = schema;
      this.actionError = '';
      this.view = VIEW.CONFIRM_DELETE;
    },

    /** Build the schema object from the sub-form. Create mints a new uuid; Edit keeps editingId.
     *  Branches on the kind toggle: a category emits `entries` + `noData` (no unit); a number emits
     *  connected `ranges` + `unit`. Both carry the dashboard source tag. */
    buildSchema() {
      const base = {
        id: this.editingId || $uuidv4(),
        name: String(this.editName || '').trim(),
        source: SCHEMA_SOURCE.DASHBOARD,
      };
      if (this.isEditingCategory) {
        return {
          ...base,
          appliesTo: APPLIES_TO.CATEGORY,
          entries: this.rowsToEntries(this.editEntries),
          noData: { label: 'No data' },
        };
      }
      return {
        ...base,
        appliesTo: APPLIES_TO.NUMBER,
        unit: this.normalizeUnitInput(this.editUnit),
        ranges: this.bandsToRanges(this.editBands),
      };
    },

    /** Map the unit combobox input to the stored value:
     *   • blank/whitespace → UNIT.COUNT (unitless; keeps validateSchema's non-empty-unit rule happy),
     *   • a known UNIT enum value (case-insensitive) → its canonical form (unit conversion keeps working),
     *   • any other free text → the trimmed string (a display-only unit label; no conversion). */
    normalizeUnitInput(raw) {
      const trimmed = String(raw == null ? '' : raw).trim();
      if (trimmed === '') return UNIT.COUNT;
      const known = Object.values(UNIT).find(u => u.toLowerCase() === trimmed.toLowerCase());
      return known || trimmed;
    },

    saveSchema() {
      const err = this.validateEdit();
      if (err) {
        log('ColoringSchemasManager', 'save blocked by validation', this.editKind, err);
        this.actionError = err;
        return;
      }
      const built = this.buildSchema();
      log('ColoringSchemasManager', 'building schema', built.appliesTo, built.id, built.unit || '');
      const next = this.editingId
        ? this.schemas.map(s => (s.id === this.editingId ? built : s))
        : [...this.schemas, built];
      this.persist(next).then(() => { this.view = VIEW.LIST; }).catch(() => {});
    },

    doDelete() {
      const id = this.deleteTarget && this.deleteTarget.id;
      const next = this.schemas.filter(s => s.id !== id);
      this.persist(next).then(() => { this.view = VIEW.LIST; this.deleteTarget = null; }).catch(() => {});
    },

    /** Persist a schema list through the shared fresh-GET-merge-PUT helper: the base config is re-read
     *  at save time so a concurrent layout/width save (or an unknown future key) is preserved, and a
     *  failed base read ABORTS the save (nothing is written) rather than overwriting the tab with a
     *  partial blob. Binds the OPEN-time tab + token so a tab switch mid-save cannot redirect the
     *  write or clobber a newer session's state. Only replaces in-memory schemas + emits on success. */
    persist(nextSchemas) {
      this.saving = true;
      this.actionError = '';
      const tabId = this._sessionTabId;
      const token = this._openToken;
      return dashboardConfigurationService
        .updateTabConfiguration(tabId, { formatVersion: CONFIG_FORMAT_VERSION, coloringSchemas: nextSchemas })
        .then((savedConfig) => {
          if (token !== this._openToken) return; // a newer open() superseded this session; don't clobber it
          this.schemas = nextSchemas;
          this.$emit('saved', JSON.stringify(savedConfig));
          log('ColoringSchemasManager', 'saved', tabId, `${nextSchemas.length} schemas`);
        })
        .catch((err) => {
          error('ColoringSchemasManager', 'save failed', tabId, err);
          if (token === this._openToken) this.actionError = 'Could not save. Please try again.';
          throw err;
        })
        .finally(() => { this.saving = false; });
    },

    /** Save gate: name required + unique within the tab (validateSchema does NOT check name), then
     *  the canonical validateSchema over the built definition, surfaced as friendly copy. */
    validateEdit() {
      const name = String(this.editName || '').trim();
      if (!name) return 'Enter a name for the schema.';
      const dup = this.schemas.some(s => s.id !== this.editingId
        && String(s.name || '').trim().toLowerCase() === name.toLowerCase());
      if (dup) return 'A schema with this name already exists on this tab.';
      const built = this.buildSchema();
      const { valid } = validateSchema(built);
      if (valid) return null;
      return this.isEditingCategory
        ? this.friendlyEntryError(built.entries || [])
        : this.friendlyBandError(built.ranges || []);
    },

    /** Map the common band problems to plain copy (validateSchema's raw messages are debug-only). */
    friendlyBandError(ranges) {
      if (!ranges.length) return 'Add at least one band.';
      for (let i = 0; i < ranges.length - 1; i++) {
        if (ranges[i].to == null) return 'Enter a number for each band boundary.';
      }
      for (let i = 1; i < ranges.length - 1; i++) {
        if (ranges[i].to <= ranges[i - 1].to) return 'Each band boundary must be higher than the one above it.';
      }
      if (ranges.some(r => r.tone == null && r.color == null)) return 'Pick a color for each band.';
      if (ranges.some(r => !String(r.label || '').trim())) return 'Give each band a short label.';
      return 'This schema has overlapping or invalid ranges.';
    },

    /** Category counterpart to friendlyBandError — never uses band-boundary language. */
    friendlyEntryError(entries) {
      if (!entries.length) return 'Add at least one label.';
      if (entries.some(e => !String(e.value || '').trim())) return 'Enter text for each label.';
      const seen = new Set();
      for (const entry of entries) {
        const key = String(entry.value || '').trim().toLowerCase();
        if (seen.has(key)) return 'Each label must be unique.';
        seen.add(key);
      }
      if (entries.some(e => !(e.color && e.color.light))) return 'Pick a color for each label.';
      return 'This schema has invalid labels.';
    },

    // ── Band <-> range helpers (auto-chained half-open [from, to); §4.2) ──
    defaultBands() {
      return [
        { to: null, tone: 'good', color: null, label: 'Good' },
        { to: null, tone: 'critical', color: null, label: 'Critical' },
      ];
    },

    bandsToRanges(bands) {
      const rows = Array.isArray(bands) ? bands : [];
      return rows.map((band, i) => {
        const range = {
          from: i === 0 ? null : numOrNull(rows[i - 1].to),
          to: i === rows.length - 1 ? null : numOrNull(band.to),
          label: String(band.label || '').trim(),
        };
        if (band.tone) range.tone = band.tone;
        else if (band.color) range.color = band.color;
        return range;
      });
    },

    rangesToBands(ranges) {
      if (!ranges.length) return this.defaultBands();
      return ranges.map(r => ({
        to: r.to == null ? null : r.to,
        tone: r.tone || null,
        color: r.color || null,
        label: r.label || '',
      }));
    },

    // ── Entry <-> row helpers (independent category rows; §4.2 exact-match `entries`) ──
    defaultEntries() {
      // Two distinct starter colors so the swatch strip is meaningful before the user edits.
      return [
        { value: '', color: { light: TONE_SWATCH.good, dark: TONE_SWATCH.good } },
        { value: '', color: { light: TONE_SWATCH.critical, dark: TONE_SWATCH.critical } },
      ];
    },

    /** Stored entries -> editor rows. A tone-based (built-in style) entry is flattened to its swatch. */
    entriesToRows(entries) {
      if (!entries.length) return this.defaultEntries();
      return entries.map(e => ({
        value: e.value == null ? '' : String(e.value),
        color: this.entryColor(e),
      }));
    },

    entryColor(entry) {
      if (entry.color && entry.color.light) {
        return { light: entry.color.light, dark: entry.color.dark || entry.color.light };
      }
      if (entry.tone && TONE_SWATCH[entry.tone]) {
        const hex = TONE_SWATCH[entry.tone];
        return { light: hex, dark: hex };
      }
      return { light: NEUTRAL_SWATCH, dark: NEUTRAL_SWATCH };
    },

    /** Editor rows -> canonical `entries` ({value, color:{light,dark}, label}) — passes validateCategoryEntries. */
    rowsToEntries(rows) {
      const list = Array.isArray(rows) ? rows : [];
      return list.map(row => {
        const value = String(row.value || '').trim();
        const color = this.rowColorPair(row);
        const entry = color ? { value, color } : { value };
        if (value) entry.label = value;
        return entry;
      });
    },

    rowColorPair(row) {
      if (row.color && row.color.light) {
        return { light: row.color.light, dark: row.color.dark || row.color.light };
      }
      return null;
    },

    /** Read-only preview swatches + labels for a schema in the list (category entries or number ranges). */
    bandPreview(schema) {
      if (schema.appliesTo === APPLIES_TO.CATEGORY) {
        const entries = Array.isArray(schema.entries) ? schema.entries : [];
        return entries.map(e => ({ label: e.label || e.value || '—', swatch: this.paintSwatch(e) }));
      }
      const ranges = Array.isArray(schema.ranges) ? schema.ranges : [];
      return ranges.map(r => ({ label: r.label || this.rangeLabel(r, schema.unit), swatch: this.paintSwatch(r) }));
    },

    isCategorySchema(schema) {
      return !!schema && schema.appliesTo === APPLIES_TO.CATEGORY;
    },

    schemaKindLabel(schema) {
      return this.isCategorySchema(schema) ? 'Label colors' : 'Number bands';
    },

    // Shared editor-preview swatch resolver (tone swatch | raw color | neutral).
    paintSwatch,

    rangeLabel(range, unit) {
      const u = unit && unit !== UNIT.COUNT ? ` ${unit}` : '';
      if (range.from == null) return `under ${range.to}${u}`;
      if (range.to == null) return `${range.from}${u} and up`;
      return `${range.from} to ${range.to}${u}`;
    },

    unitLabel(unit) {
      return unit === UNIT.COUNT ? 'count (no unit)' : unit;
    },
  },
};
</script>

<style lang="scss" scoped>
.csm {
  font-family: var(--ds-font-family-body, 'IBM Plex Sans', sans-serif);
  overflow: hidden;
}

.csm__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 18px;
  border-bottom: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
}

.csm__eyebrow {
  display: block;
  margin-bottom: 5px;
  color: var(--v-primary-base);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.csm__title {
  display: flex;
  align-items: center;
  font-family: var(--ds-font-family-heading, 'Poppins', sans-serif);
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.015em;
}

.csm__subtitle {
  max-width: 620px;
  margin: 5px 0 0;
  color: var(--v-text-base);
  font-size: 12.5px;
  line-height: 1.45;
}

.csm__body {
  padding: 24px !important;
  min-height: 260px;
}

.csm__center {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 180px;
}

.csm__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 44px 24px;
  border: 1px dashed var(--ds-border-default, rgba(0, 0, 0, 0.12));
  border-radius: var(--ds-radius-md, 8px);
  background: var(--ds-surface-hover, rgba(3, 98, 76, 0.04));
}

.csm__empty-icon {
  margin-bottom: 10px;
  color: var(--v-primary-base);
  opacity: 0.72;
}

.csm__empty-text {
  font-size: 13px;
  line-height: 1.5;
  max-width: 380px;
  margin: 0;
}

.csm__empty-action {
  margin-top: 16px;
}

.csm__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.csm__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
  border-radius: var(--ds-radius-md, 8px);
  transition: border-color var(--ds-transition-default, 180ms ease), box-shadow var(--ds-transition-default, 180ms ease);
}

.csm__row:hover {
  border-color: rgba(3, 98, 76, 0.3);
  box-shadow: var(--ds-shadow-1, 0 2px 6px rgba(27, 52, 45, 0.08));
}

.csm__row-name {
  font-size: 13.5px;
  font-weight: 600;
}

.csm__row-meta {
  font-size: 11.5px;
  opacity: 0.6;
  margin-top: 1px;
}

.csm__strip {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 6px;
}

.csm__band {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  opacity: 0.85;
}

.csm__swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 4px;
  flex: 0 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.csm__row-actions {
  display: flex;
  flex: 0 0 auto;
}

.csm__add {
  margin-top: 12px;
  text-transform: none;
  letter-spacing: normal;
}

.csm__kind {
  margin-bottom: 20px;
}

.csm__kind-toggle {
  margin-top: 7px;
}

.csm__kind-toggle .v-btn {
  text-transform: none;
  letter-spacing: normal;
}

.csm__form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(190px, .8fr);
  gap: 16px;
}

.csm__form-grid--single {
  grid-template-columns: 1fr;
}

.csm__label {
  display: block;
  font-size: 12.5px;
  font-weight: 700;
  margin-bottom: 7px;
  color: var(--v-text-base);
}

.csm__label--mt {
  margin-top: 16px;
}

.csm__hint {
  display: block;
  margin-top: 6px;
  color: #52615c;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.45;
}

.csm__hint--mt {
  margin-top: 10px;
}

.csm__hint--inline {
  margin-top: 0;
}

.csm__ranges-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-top: 22px;
  margin-bottom: 10px;
}

.csm__ranges-head .csm__label {
  margin-bottom: 3px;
}

.csm__ranges-badge {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 4px;
  padding: 3px 7px;
  border: 1px solid rgba(3, 98, 76, 0.2);
  border-radius: var(--ds-radius-full, 999px);
  background: rgba(3, 98, 76, 0.06);
  color: var(--v-primary-base);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.csm__error {
  display: flex;
  align-items: center;
  font-size: 12px;
  margin: 0;
  color: #a07a00;
}

.csm__error--mt {
  margin-top: 10px;
}

.csm__confirm {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 12px;
}

.csm__confirm-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 6px;
}

.csm__confirm-text {
  font-size: 12.5px;
  opacity: 0.75;
  max-width: 400px;
  margin: 0 0 8px;
}

.csm__foot {
  padding: 14px 24px;
  border-top: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
  background: var(--ds-surface-hover, rgba(3, 98, 76, 0.02));
}

.theme--dark {
  .csm__head,
  .csm__foot {
    border-color: rgba(255, 255, 255, 0.1);
  }

  .csm__row {
    border-color: rgba(255, 255, 255, 0.1);
  }

  .csm__empty {
    border-color: rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.03);
  }

  .csm__swatch {
    border-color: rgba(255, 255, 255, 0.2);
  }

  .csm__error {
    color: #d2a000;
  }

  .csm__hint {
    color: rgba(255, 255, 255, 0.78);
  }
}

@media (max-width: 640px) {
  .csm__head,
  .csm__body,
  .csm__foot {
    padding-right: 16px !important;
    padding-left: 16px !important;
  }

  .csm__form-grid {
    grid-template-columns: 1fr;
  }

  .csm__ranges-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
}
</style>
